import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, errorMessageFromBody, isNetworkError, NETWORK_ERROR_MESSAGE } from '../../../services/api';
import { isTrustedOAuthMessage } from '../../../utils/oauthMessage';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

interface AudienceCluster {
  segment: string;
  percentage: number;
  // The segment's share of the average interactions per post — a count, not %.
  engagement: number;
  trend: string;
  description: string;
}

interface SentimentCluster {
  type: string;
  percentage: number;
  volume: number;
  trend: string;
  examples: string[];
}

interface ContentTypeItem {
  type: string;
  score: number;
  // Average interactions (likes + comments) per post — a count, not %.
  engagement: number;
  // null when the platform reports no reach.
  reach: number | null;
  bestFor?: string | null;
}

interface ContentThemeItem {
  theme: string;
  score: number;
  // Average interactions (likes + comments) per post — a count, not %.
  engagement: number;
  posts: number;
  bestType: string;
}

interface PlatformData {
  id: string;
  name: string;
  icon: string;
  clusters: AudienceCluster[];
  sentiment: SentimentCluster[];
  contentTypes: ContentTypeItem[];
  contentThemes: ContentThemeItem[];
  needsReconnect?: boolean;
  profileUrl?: string;
  // The AI clustering step failed this time — clusters is empty.
  clustersUnavailable?: boolean;
  // Some posts couldn't be classified this time — themes cover the rest only.
  themesUnavailable?: boolean;
}

interface AudienceIntelligenceData {
  platforms: PlatformData[];
  // When the shown report was made, and whether a newer one is on its way.
  updatedAt?: string | null;
  refreshing?: boolean;
  // Nothing stored yet: the first report is being prepared (platforms is empty).
  preparing?: boolean;
}

// Unknown reach (platform reports none) is shown as a dash, never as 0.
const formatReach = (reach?: number | null): string =>
  reach === null || reach === undefined ? '—' : reach.toLocaleString();

export default function AudienceContentIntelligence() {
  const [activePlatform, setActivePlatform] = useState(0);
  const [activeView, setActiveView] = useState<'audience' | 'performance'>('audience');
  const [intelligenceData, setIntelligenceData] = useState<AudienceIntelligenceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  const fetchAudienceData = () => {
    const userId = localStorage.getItem('user_id') || '';
    const token = localStorage.getItem('access_token') || '';

    if (!userId) {
      setIsLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/smm/strategy/audience-intelligence/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errorMessageFromBody(errBody, res.status));
        }
        return res.json();
      })
      .then((data) => {
        setIntelligenceData(data);
        setFetchError(null);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('[AudienceIntelligence] fetch error:', err);
        setFetchError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Please try again.');
        setIsLoading(false);
      });
  };

  const retryFetch = () => {
    setIsLoading(true);
    setFetchError(null);
    fetchAudienceData();
  };

  useEffect(() => {
    fetchAudienceData();
  }, []);

  // Re-ask quietly (no skeleton) and swap the answer in. refresh=true is the
  // Refresh button: the server starts a newer analysis in the background.
  const refetchQuietly = async (refresh = false) => {
    const userId = localStorage.getItem('user_id') || '';
    const token = localStorage.getItem('access_token') || '';
    if (!userId) return;
    const res = await fetch(
      `${API_BASE_URL}/smm/strategy/audience-intelligence/${userId}${refresh ? '?refresh=true' : ''}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    if (res.ok) setIntelligenceData(await res.json());
  };
  // The stored report is shown at once; while the server analyses newer data
  // (or prepares the first report) keep asking and swap it in.
  const freshness = useBackgroundRefresh(intelligenceData?.refreshing, () => refetchQuietly());
  const preparing = useBackgroundRefresh(intelligenceData?.preparing, () => refetchQuietly(), PREPARING_POLL);

  // Add OAuth message listener
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (!isTrustedOAuthMessage(event)) return;
      if (event.data?.type === 'SMM_OAUTH_CODE') {
        const platform = event.data.platform;
        const code = event.data.code;
        if (platform && code) {
          await handleReconnectCode(platform, code);
        }
      } else if (event.data?.type === 'SMM_OAUTH_ERROR') {
        setConnectingId(null);
        alert(`Connection failed: ${event.data.error || 'Failed to authenticate platform'}`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [intelligenceData, activePlatform]);

  const handleReconnectClick = async (platformName: string) => {
    const userId = localStorage.getItem('user_id') || '';
    if (!userId) return;

    setConnectingId(platformName);
    const token = localStorage.getItem('access_token') || '';

    try {
      const res = await fetch(`${API_BASE_URL}/smm/onboarding/oauth/url?platform=${platformName}&user_id=${userId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errorMessageFromBody(errBody, res.status));
      }
      const data = await res.json();

      if (data && data.authorization_url) {
        const popup = window.open(
          data.authorization_url,
          `${platformName}_oauth`,
          'width=600,height=700,left=100,top=100'
        );
        if (!popup) {
          setConnectingId(null);
          alert('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.');
          return;
        }
        popupRef.current = popup;
        
        const poll = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(poll);
            setConnectingId((prev) => (prev === platformName ? null : prev));
          }
        }, 500);
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (err: any) {
      setConnectingId(null);
      alert(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Failed to initiate reconnection');
    }
  };

  const handleReconnectCode = async (platformName: string, code: string) => {
    const userId = localStorage.getItem('user_id') || '';
    if (!userId) return;
    const token = localStorage.getItem('access_token') || '';

    const platformObj = intelligenceData?.platforms?.find(
      (p) => p.id.toLowerCase() === platformName.toLowerCase()
    );
    const profileUrl = platformObj?.profileUrl || '';

    try {
      const response = await fetch(`${API_BASE_URL}/smm/onboarding/step1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id: userId,
          platforms: [
            {
              platform: platformName.toLowerCase(),
              profile_url: profileUrl,
              oauth_code: code,
              connected: true
            }
          ],
          permissions: {
            reach_analytics: true,
            audience_demographics: true,
            comment_visibility: true,
            watch_time_subscriptions: true
          }
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errorMessageFromBody(errData, response.status));
      }

      // Reconnection successful! Force refresh of data
      fetchAudienceData();
      setConnectingId(null);
    } catch (err: any) {
      setConnectingId(null);
      alert(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Failed to reconnect channel');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center justify-between gap-4">
          <div className="flex bg-gray-100 rounded-lg p-0.5 gap-1">
            <div className="h-9 bg-gray-200 rounded-md w-44" />
            <div className="h-9 bg-gray-100 rounded-md w-44" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="h-2.5 bg-gray-300 rounded w-16 mb-3" />
          <div className="flex flex-wrap gap-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-9 bg-gray-200 rounded-lg w-24" />
            ))}
          </div>
        </div>
        <div>
          <div className="h-5 bg-gray-200 rounded w-52 mb-4" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-4 bg-gray-200 rounded w-28" />
                  <div className="w-4 h-4 bg-gray-200 rounded" />
                </div>
                <div className="h-3 bg-gray-100 rounded w-full mb-1.5" />
                <div className="h-3 bg-gray-100 rounded w-4/5 mb-4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="text-center py-12 text-red-500">
        <p className="font-semibold">Failed to load audience intelligence.</p>
        <p className="text-sm mt-1">{fetchError}</p>
        <button
          onClick={retryFetch}
          className="mt-3 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Nothing stored yet: the first report is being prepared — not "no platforms".
  if (intelligenceData?.preparing) {
    return (
      <PreparingNotice
        state={preparing}
        onRetry={() => {
          setIntelligenceData(null); // lets the polling start over
          retryFetch();
        }}
      />
    );
  }

  // An empty platform list means only "nothing connected" — backend failures
  // arrive as an error status and land in the fetchError state above.
  if (!intelligenceData || !intelligenceData.platforms || intelligenceData.platforms.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No platforms connected yet. Connect your social accounts to see audience intelligence.
      </div>
    );
  }

  const platform = intelligenceData.platforms[activePlatform] || intelligenceData.platforms[0];
  const behaviorClusters = platform?.clusters || [];
  const sentimentClusters = platform?.sentiment || [];
  const contentTypes = platform?.contentTypes || [];
  const contentThemes = platform?.contentThemes || [];

  const topContentType = contentTypes.length > 0 ? contentTypes[0] : null;
  const topContentTheme = contentThemes.length > 0 ? contentThemes[0] : null;

  const trendIcon = (t: string) => {
    if (t === 'up') return <i className="ri-arrow-up-line text-emerald-500 w-4 h-4 flex items-center justify-center"></i>;
    if (t === 'down') return <i className="ri-arrow-down-line text-red-500 w-4 h-4 flex items-center justify-center"></i>;
    return <i className="ri-subtract-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>;
  };

  const trendBg = (t: string) => {
    if (t === 'up') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (t === 'down') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 via-white to-blue-50/20 opacity-70 pointer-events-none" />
        <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-blue-50 blur-3xl opacity-50" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-teal-50 blur-3xl opacity-50" />
        <div className="relative mb-6 z-10">
          <div className="w-20 h-20 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shadow-inner animate-pulse">
            <i className="ri-brain-line text-4xl text-blue-600"></i>
          </div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
            <i className="ri-time-line text-white text-sm"></i>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">Audience Intelligence</h3>
        <p className="text-sm text-gray-500 max-w-md mb-8 leading-relaxed relative z-10">
          Map audience behavior, track sentiment analysis, and optimize content themes across social channels. This feature will be available in the next release.
        </p>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50/70 border border-blue-100 text-xs font-semibold text-blue-700 relative z-10">
          <i className="ri-compass-3-line"></i>
          Platform Integration Underway
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DataFreshnessBanner updatedAt={intelligenceData.updatedAt} state={freshness} onRefresh={() => refetchQuietly(true)} />

      {/* View Switcher */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {[
            { id: 'audience', label: 'Audience Preference Map', icon: 'ri-user-community-line' },
            { id: 'performance', label: 'Content Type Performance', icon: 'ri-bar-chart-grouped-line' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as typeof activeView)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 ${activeView === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <i className={`${tab.icon} w-4 h-4 flex items-center justify-center`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Platform Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Channel</p>
        <div className="flex flex-wrap gap-2">
          {(intelligenceData?.platforms || []).map((p: any, i: number) => (
            <button
              key={p.id}
              onClick={() => setActivePlatform(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activePlatform === i
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <i className={`${p.icon} text-base`}></i>
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {platform?.needsReconnect ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-slate-50 border border-amber-200 rounded-2xl p-8 text-center relative overflow-hidden shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-tr from-amber-50/20 via-white to-orange-50/10 opacity-70 pointer-events-none" />
          <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-amber-50 blur-3xl opacity-50" />
          <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-orange-50 blur-3xl opacity-50" />
          <div className="relative mb-6 z-10">
            <div className="w-20 h-20 bg-white border border-amber-100 rounded-2xl flex items-center justify-center shadow-md">
              <i className={`${platform.icon} text-4xl text-amber-500`}></i>
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
              <i className="ri-error-warning-line text-white text-sm"></i>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">Connection Expired</h3>
          <p className="text-sm text-gray-500 max-w-md mb-8 leading-relaxed relative z-10">
            Your {platform.name} authentication has expired or been invalidated. Reconnect your social account to resume mapping audience behavior, tracking sentiment analysis, and optimizing content formats.
          </p>
          <button
            onClick={() => handleReconnectClick(platform.id.toLowerCase())}
            disabled={connectingId !== null}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0859B8] hover:bg-[#074a9b] disabled:opacity-50 text-white rounded-xl text-sm font-bold shadow-md transition-all hover:shadow-lg cursor-pointer relative z-10"
          >
            {connectingId === platform.id.toLowerCase() ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Connecting...
              </>
            ) : (
              <>
                <i className="ri-refresh-line"></i>
                Reconnect {platform.name}
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          {/* ===== AUDIENCE PREFERENCE MAP ===== */}
          {activeView === 'audience' && (
            <div className="space-y-6">
              {/* Audience Behavior Clustering */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Audience Behavior Clustering</h3>
                {platform?.clustersUnavailable ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 text-center text-gray-500 text-sm">
                    <p>Audience clusters are unavailable right now.</p>
                    <button
                      onClick={retryFetch}
                      className="mt-3 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                ) : behaviorClusters.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 text-center text-gray-500 text-sm">
                    No audience behavior clusters found for this channel.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {behaviorClusters.map((cluster: any) => (
                      <div key={cluster.segment} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 text-sm">{cluster.segment}</h4>
                          {trendIcon(cluster.trend)}
                        </div>
                        <p className="text-xs text-gray-500 mb-4 leading-relaxed">{cluster.description}</p>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500">Share</span>
                          <span className="text-sm font-bold text-gray-900">{cluster.percentage}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${cluster.percentage}%` }}></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Est. interactions / post</span>
                          <span className="text-sm font-bold text-gray-900">{cluster.engagement}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sentiment Intelligence Layer */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Sentiment Intelligence Layer</h3>
                {platform?.commentsStatus && sentimentClusters.length === 0 ? (
                  <CommentsEmptyState status={platform.commentsStatus} what="Sentiment" onRetry={retryFetch} />
                ) : sentimentClusters.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-xl p-5 text-center text-gray-500 text-sm">
                    No sentiment data analyzed for this channel.
                  </div>
                ) : (
                  <>
                  {platform?.commentsPartial && (
                    <p className="text-xs text-amber-700 mb-3">
                      Some posts' comments couldn't be read, so this covers only part of your recent comments.
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {sentimentClusters.map((sentiment: any) => (
                      <div key={sentiment.type} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 text-sm">{sentiment.type}</h4>
                          {trendIcon(sentiment.trend)}
                        </div>
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-2xl font-bold text-gray-900">{sentiment.percentage}%</span>
                          <span className="text-xs text-gray-500">of comments</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">{(sentiment.volume || 0).toLocaleString()} interactions</p>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                          <div
                            className={`h-full rounded-full ${sentiment.type === 'Positive Feedback'
                                ? 'bg-emerald-500'
                                : sentiment.type === 'Queries'
                                  ? 'bg-sky-500'
                                  : sentiment.type === 'Complaints'
                                    ? 'bg-red-400'
                                    : 'bg-amber-500'
                              }`}
                            style={{ width: `${sentiment.percentage}%` }}
                          ></div>
                        </div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Recent Examples</p>
                        <div className="space-y-1.5">
                          {(sentiment.examples || []).map((ex: string, i: number) => (
                            <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-1.5 italic leading-relaxed">
                              "{ex}"
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ===== CONTENT TYPE PERFORMANCE ===== */}
          {activeView === 'performance' && (
            <div className="space-y-6">
              {/* Top Performer Banner */}
              {topContentType ? (
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-5 text-white">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Best Performing Format on {platform.name}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold">{topContentType.type}</span>
                        <span className="text-sm text-emerald-400 font-medium">{topContentType.engagement} avg interactions / post</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {topContentType.bestFor ? `Best for: ${topContentType.bestFor} · ` : ''}
                        {formatReach(topContentType.reach)} avg reach
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Best Theme</p>
                        <p className="text-sm font-bold">
                          {topContentTheme ? topContentTheme.theme : platform.themesUnavailable ? 'Classification unavailable' : '—'}
                        </p>
                      </div>
                      <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                        <i className="ri-trophy-line text-amber-400"></i>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center text-gray-500 text-sm">
                  Not enough data to determine top performers yet.
                </div>
              )}

              {/* Content Type Rankings */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-1">Content Type Performance</h3>
                  <p className="text-xs text-gray-500 mb-4">Which format performs best on {platform.name}</p>
                  {contentTypes.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">No content format performance data available.</p>
                  ) : (
                    <div className="space-y-3">
                      {contentTypes.map((ct: any, idx: number) => (
                        <div key={ct.type}>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-xs font-bold text-gray-400 w-5">{idx + 1}</span>
                            <span className="text-sm font-medium text-gray-700 w-24">{ct.type}</span>
                            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${idx === 0 ? 'bg-emerald-500' : idx === 1 ? 'bg-blue-500' : idx === 2 ? 'bg-sky-400' : 'bg-gray-300'}`}
                                style={{ width: `${(ct.score / 10) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 w-8 text-right">{ct.score}</span>
                          </div>
                          <div className="flex items-center gap-3 ml-8">
                            <span className="text-xs text-gray-500">{ct.engagement} avg interactions</span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-gray-500">{formatReach(ct.reach)} avg reach</span>
                            {ct.bestFor && (
                              <>
                                <span className="text-xs text-gray-400">·</span>
                                <span className="text-xs text-blue-600 font-medium">{ct.bestFor}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Content Theme Rankings */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-1">Content Theme Performance</h3>
                  <p className="text-xs text-gray-500 mb-4">Which theme resonates most on {platform.name}</p>
                  {platform.themesUnavailable && contentThemes.length > 0 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                      Some posts couldn't be classified right now, so this ranking covers only the classified posts.
                    </p>
                  )}
                  {contentThemes.length === 0 ? (
                    platform.themesUnavailable ? (
                      <div className="text-sm text-gray-500 py-6 text-center">
                        <p>Theme classification is unavailable right now.</p>
                        <button
                          onClick={retryFetch}
                          className="mt-3 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-6 text-center">No content theme performance data available.</p>
                    )
                  ) : (
                    <div className="space-y-3">
                      {contentThemes.map((ct: any, idx: number) => (
                        <div key={ct.theme}>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-xs font-bold text-gray-400 w-5">{idx + 1}</span>
                            <span className="text-sm font-medium text-gray-700 w-28">{ct.theme}</span>
                            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${idx === 0 ? 'bg-emerald-500' : idx === 1 ? 'bg-blue-500' : idx === 2 ? 'bg-sky-400' : 'bg-gray-300'}`}
                                style={{ width: `${(ct.score / 10) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 w-8 text-right">{ct.score}</span>
                          </div>
                          <div className="flex items-center gap-3 ml-8">
                            <span className="text-xs text-gray-500">{ct.engagement} avg interactions</span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-gray-500">{ct.posts} posts</span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-blue-600 font-medium">via {ct.bestType || ct.best_type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
